/** @file Soft checks of a clip on a puppet: nodes the puppet lacks are warnings, never errors (design 4.4). */
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';

/** Every (node name, path) the clip refers to, in document order. */
function references(clip: Clip): [string, string][] {
  const refs: [string, string][] = [];
  clip.keys.forEach((key, i) => {
    for (const [node, nodeKey] of Object.entries(key.nodes)) {
      refs.push([node, `/keys/${i}/nodes/${node}`]);
      if (typeof nodeKey.parent === 'string') refs.push([nodeKey.parent, `/keys/${i}/nodes/${node}/parent`]);
    }
    (key.events ?? []).forEach((e, j) => { if (e.node !== undefined) refs.push([e.node, `/keys/${i}/events/${j}/node`]); });
  });
  for (const node of Object.keys(clip.tracks ?? {})) refs.push([node, `/tracks/${node}`]);
  return refs;
}

/** One `UNKNOWN_NODE_IN_CLIP` per node name the clip uses and the puppet lacks, at its first use. */
export function validateClipAgainstPuppet(clip: Clip, puppet: Puppet): Warning[] {
  const reported = new Set<string>();
  const warnings: Warning[] = [];
  for (const [node, path] of references(clip)) {
    if (Object.hasOwn(puppet.nodes, node) || reported.has(node)) continue;
    reported.add(node);
    warnings.push({
      code: 'UNKNOWN_NODE_IN_CLIP', path, params: { node },
      message: `clip '${clip.name}' uses node '${node}', which puppet '${puppet.name}' does not have; it is skipped`,
      hint: 'rename the node in the clip, or add it to the puppet',
    });
  }
  return warnings;
}
